/**
 * Device color utility functions for maintaining consistent colors across the application
 */

// AuxType constants from the Kotlin enum class
export enum AuxTypeCode {
  EMPTY = 0,

  // pool
  AUX = 1,
  POOL_HEATER = 2,
  POOL_PRESSURE_CLEANER = 3,
  POOL_IN_FLOOR_CLEANING = 4,

  // dosing system
  SALT_CHLORINATOR = 5,
  PH_DOSER = 6,
  MINERAL_CHLORINATOR = 7,
  LIQUID_CHLORINE = 8,
  NAKED_IONISER = 9,
  OZONE = 10,
  UV = 11,

  // lights
  ASTRAL_POOL_LIGHT = 12,
  AQUAQUIP_LIGHT = 13,
  POOLRITE_LIGHT = 14,
  SPA_ELECTRICS_LIGHT = 15,
  ZODIAC = 16,

  // solar type code
  SOLAR_DEDICATED = 17,
  SOLAR_FILTER_PUMP = 18,

  // spa type code
  SPA_HEATER = 19,
  SPA_ACTUATOR_RETURN = 20,
  SPA_ACTUATOR_SUCTION = 21,
  SPA_JETS_1 = 22,
  SPA_JETS_2 = 23,
  SPA_BLOWER_1 = 24,
  SPA_BLOWER_2 = 25,
  SPA_ASTRAL_POOL_LIGHT = 26,
  SPA_AQUAQUIP_LIGHT = 27,
  SPA_POOLRITE_LIGHT = 28,
  SPA_SPA_ELECTRICS_LIGHT = 29,
  SPA_ZODIAC = 30,

  // pump
  SPALSHME_PUMP = 31,

  // combine heater for pool + spa
  COMBINE_POOL_SPA_HEATER = 32,

  SPA_ACTUATOR_EXTRA_1 = 33,
  SPA_ACTUATOR_EXTRA_2 = 34,
  RS485 = 999,
}

// Color palette constants
const COLORS = {
  // Primary colors
  BLUE: '#3B82F6',
  EMERALD: '#10B981',
  AMBER: '#F59E0B',
  RED: '#EF4444',
  VIOLET: '#8B5CF6',
  PINK: '#EC4899',
  CYAN: '#06B6D4',
  LIME: '#65A30D',
  LIGHT_BLUE: '#0EA5E9',
  FUCHSIA: '#D946EF',
  
  // Secondary colors
  INDIGO: '#6366F1',
  TEAL: '#14B8A6',
  GREEN: '#22C55E',
  ORANGE: '#F97316',
  YELLOW: '#EAB308',
  ROSE: '#F43F5E',
  PURPLE: '#A855F7',
  SKY: '#0284C7',
};

// Maps AuxType to color - using a fixed color scheme for consistency
const AUX_TYPE_COLOR_MAP: Record<number, string> = {
  // Default (empty)
  [AuxTypeCode.EMPTY]: '#999999',
  
  // Pumps (blue shades)
  [AuxTypeCode.SPALSHME_PUMP]: COLORS.BLUE,
  
  // Heaters (red shades)
  [AuxTypeCode.POOL_HEATER]: COLORS.RED,
  [AuxTypeCode.SPA_HEATER]: COLORS.ORANGE,
  [AuxTypeCode.COMBINE_POOL_SPA_HEATER]: COLORS.ROSE,
  
  // Lights (yellow/amber shades)
  [AuxTypeCode.ASTRAL_POOL_LIGHT]: COLORS.AMBER,
  [AuxTypeCode.AQUAQUIP_LIGHT]: COLORS.AMBER,
  [AuxTypeCode.POOLRITE_LIGHT]: COLORS.AMBER,
  [AuxTypeCode.SPA_ELECTRICS_LIGHT]: COLORS.AMBER,
  [AuxTypeCode.ZODIAC]: COLORS.AMBER,
  [AuxTypeCode.SPA_ASTRAL_POOL_LIGHT]: COLORS.LIGHT_BLUE, 
  [AuxTypeCode.SPA_AQUAQUIP_LIGHT]: COLORS.LIGHT_BLUE,
  [AuxTypeCode.SPA_POOLRITE_LIGHT]: COLORS.LIGHT_BLUE,
  [AuxTypeCode.SPA_SPA_ELECTRICS_LIGHT]: COLORS.LIGHT_BLUE,
  [AuxTypeCode.SPA_ZODIAC]: COLORS.LIGHT_BLUE,
  
  // Cleaners (green shades)
  [AuxTypeCode.POOL_PRESSURE_CLEANER]: COLORS.GREEN,
  [AuxTypeCode.POOL_IN_FLOOR_CLEANING]: COLORS.EMERALD,
  
  // Sanitizers (purple shades)
  [AuxTypeCode.SALT_CHLORINATOR]: COLORS.VIOLET,
  [AuxTypeCode.PH_DOSER]: COLORS.PURPLE,
  [AuxTypeCode.MINERAL_CHLORINATOR]: COLORS.INDIGO,
  [AuxTypeCode.LIQUID_CHLORINE]: COLORS.FUCHSIA,
  [AuxTypeCode.NAKED_IONISER]: COLORS.PINK,
  [AuxTypeCode.OZONE]: COLORS.TEAL,
  [AuxTypeCode.UV]: COLORS.CYAN,
  
  // Solar (yellow shades)
  [AuxTypeCode.SOLAR_DEDICATED]: COLORS.YELLOW,
  [AuxTypeCode.SOLAR_FILTER_PUMP]: COLORS.ORANGE,
  
  // SPA (cyan/blue shades)
  [AuxTypeCode.SPA_ACTUATOR_RETURN]: COLORS.VIOLET,
  [AuxTypeCode.SPA_ACTUATOR_SUCTION]: COLORS.RED,
  [AuxTypeCode.SPA_JETS_1]: COLORS.AMBER,
  [AuxTypeCode.SPA_JETS_2]: COLORS.AMBER,
  [AuxTypeCode.SPA_BLOWER_1]: COLORS.SKY,
  [AuxTypeCode.SPA_BLOWER_2]: COLORS.SKY,
  [AuxTypeCode.SPA_ACTUATOR_EXTRA_1]: COLORS.TEAL,
  [AuxTypeCode.SPA_ACTUATOR_EXTRA_2]: COLORS.TEAL,
  
  // Auxiliary
  [AuxTypeCode.AUX]: COLORS.LIME
  ,
  [AuxTypeCode.RS485]: COLORS.TEAL
};

/**
 * Get the color for a given AuxType code
 * @param auxType - The auxiliary device type code
 * @returns Hex color code for the device type
 */
export const getDeviceTypeColor = (auxType: number): string => {
  // Return the mapped color if it exists
  if (auxType in AUX_TYPE_COLOR_MAP) {
    return AUX_TYPE_COLOR_MAP[auxType];
  }
  
  // Generate a deterministic color based on the auxType if not mapped
  const hash = Math.abs(auxType * 747382791) % 16777215;
  return `#${hash.toString(16).padStart(6, '0')}`;
};

/**
 * Get device type name from AuxType code
 * @param auxType - The auxiliary device type code
 * @returns Display name for the device type
 */
export const getDeviceTypeName = (auxType: number): string => {
  switch (auxType) {
    case AuxTypeCode.EMPTY:
      return '';
    // Pool
    case AuxTypeCode.AUX:
      return 'Auxiliary';
    case AuxTypeCode.POOL_HEATER:
      return 'Pool Heater';
    case AuxTypeCode.POOL_PRESSURE_CLEANER:
      return 'Pool Pressure Cleaner';
    case AuxTypeCode.POOL_IN_FLOOR_CLEANING:
      return 'Pool In Floor Cleaner';
    // Dosing System
    case AuxTypeCode.SALT_CHLORINATOR:
      return 'Salt Chlorinator';
    case AuxTypeCode.PH_DOSER:
      return 'pH Doser';
    case AuxTypeCode.MINERAL_CHLORINATOR:
      return 'Mineral Chlorinator';
    case AuxTypeCode.LIQUID_CHLORINE:
      return 'Liquid Chlorine';
    case AuxTypeCode.NAKED_IONISER:
      return 'Naked Ioniser';
    case AuxTypeCode.OZONE:
      return 'Ozone';
    case AuxTypeCode.UV:
      return 'UV';
    // Lights
    case AuxTypeCode.ASTRAL_POOL_LIGHT:
    case AuxTypeCode.AQUAQUIP_LIGHT:
    case AuxTypeCode.POOLRITE_LIGHT:
    case AuxTypeCode.SPA_ELECTRICS_LIGHT:
    case AuxTypeCode.ZODIAC:
      return 'Pool Lights';
    // Solar
    case AuxTypeCode.SOLAR_DEDICATED:
      return 'Solar Dedicated Pump';
    case AuxTypeCode.SOLAR_FILTER_PUMP:
      return 'Solar Filter Pump';
    // Spa
    case AuxTypeCode.SPA_HEATER:
      return 'SPA Heater';
    case AuxTypeCode.SPA_ACTUATOR_RETURN:
      return 'Spa Mode (Return)';
    case AuxTypeCode.SPA_ACTUATOR_SUCTION:
      return 'Spa Mode (Suction)';
    case AuxTypeCode.SPA_JETS_1:
    case AuxTypeCode.SPA_JETS_2:
      return 'SPA Jet';
    case AuxTypeCode.SPA_BLOWER_1:
    case AuxTypeCode.SPA_BLOWER_2:
      return 'SPA Blower';
    case AuxTypeCode.SPA_ASTRAL_POOL_LIGHT:
    case AuxTypeCode.SPA_AQUAQUIP_LIGHT:
    case AuxTypeCode.SPA_POOLRITE_LIGHT:
    case AuxTypeCode.SPA_SPA_ELECTRICS_LIGHT:
    case AuxTypeCode.SPA_ZODIAC:
      return 'Spa Lights';
    // Pump
    case AuxTypeCode.SPALSHME_PUMP:
      return 'Main Pump';
    // Combined
    case AuxTypeCode.COMBINE_POOL_SPA_HEATER:
      return 'Combined Pool & Spa Heater';
    case AuxTypeCode.SPA_ACTUATOR_EXTRA_1:
      return 'Spa Actuator Extra 1';
    case AuxTypeCode.SPA_ACTUATOR_EXTRA_2:
      return 'Spa Actuator Extra 2';
    case AuxTypeCode.RS485:
      return 'RS485';
    default:
      return `Device Type ${auxType}`;
  }
};

/**
 * Get a tuple of [color, name] for a device type
 * @param auxType - The auxiliary device type code
 * @returns Array with [color, name] for the device type
 */
export const getDeviceColorAndName = (auxType: number): [string, string] => {
  return [getDeviceTypeColor(auxType), getDeviceTypeName(auxType)];
};

/**
 * Get all available device types with their colors and names
 * @returns Array of objects with {code, color, name}
 */
export const getAllDeviceTypes = () => {
  return Object.values(AuxTypeCode)
    .filter(value => typeof value === 'number')
    .map((code: number) => ({
      code,
      color: getDeviceTypeColor(code),
      name: getDeviceTypeName(code)
    }))
    .filter(item => item.name !== ''); // Filter out EMPTY
};

export default {
  getDeviceTypeColor,
  getDeviceTypeName,
  getDeviceColorAndName,
  getAllDeviceTypes,
  AuxTypeCode
}; 
